import { useEffect, useState, useMemo, useRef } from "react";
import { QIMapLoader, QISpinner } from "../../../components";
import { Icon, Style, Stroke } from "ol/style.js";
import {
  locationImg,
  endPointMarkerNew,
  startPointMarkerNew,
  destination,
  homeLocation,
} from "../assets";
import { removeDuplicates } from "../utils";
import { useGetVehicleRoutingQuery } from "../services";
import moment from "moment";
import mapboxgl from "mapbox-gl";
import {
  getViewportDatasetKey,
  shouldAutoFitViewport,
} from "../mapViewportPolicy";

/**
 * Component for displaying vehicle trips on a map.
 * @param {Object} props - Component props.
 * @param {string} props.deviceId - ID of the device/vehicle.
 * @param {Object} props.selectedRange - Selected time range for data retrieval.
 */

const TripsMap = ({
  deviceId,
  selectedRange,
  mapExpand,
  dockAlign,
  maximizeMinimize,
  refreshMap,
  mapProvider,
  mapboxAccessToken,
  googleMapsApiKey,
  listCount,
  selectedPlateNumber,
  clientConfig,
  mapSelectionLoading = false, // New prop for trip selection loading
}) => {
  const [tripMapRef, setTripMapRef] = useState(null);
  //Sets new map everytime
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [mapTrackData, setmapTrackData] = useState([]);
  const [totalCount, setTotalCount] = useState(200);
  const [maxHdopThreshold, setMaxHdopThreshold] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const lastProcessedResDataRef = useRef(null); // Prevent re-processing same resData
  // Track the currently drawn layer name so cleanup does not depend on the
  // async `count` state (which can be stale when pages accumulate quickly and
  // would otherwise leave the final full track replaced by a partial one).
  const activeLayerRef = useRef(null);
  const fittedDatasetKeyRef = useRef(null);

  const viewportDatasetKey = getViewportDatasetKey(
    deviceId || selectedPlateNumber,
    selectedRange,
    mapProvider
  );

  useEffect(() => {
    if (clientConfig?.tracking?.web?.map?.hdop_threshold) {
      setMaxHdopThreshold(clientConfig?.tracking?.web?.map?.hdop_threshold);
    }
  }, []);

  // Memoize the filtered track data to prevent recalculation
  const validTrackData = useMemo(() => {
    if (!mapTrackData.length) return [];
    return removeDuplicates(
      mapTrackData.filter(
        (point) =>
          point &&
          typeof point.lat === "number" &&
          typeof point.lng === "number" &&
          (point.hdop == null || point.hdop <= maxHdopThreshold)
      )
    );
  }, [mapTrackData, maxHdopThreshold]);

  // Reset map data when selectedRange or deviceId changes
  useEffect(() => {
    setPage(0);
    setmapTrackData([]);
    setIsLoading(true);
    lastProcessedResDataRef.current = null; // Reset processed data tracker
    fittedDatasetKeyRef.current = null;
    // Remove any leftover layer from the previous selection so the new track
    // starts clean instead of racing with a stale layer name. Layer naming is
    // provider-specific, so route the removal through the matching API.
    if (tripMapRef && activeLayerRef.current) {
      try {
        if (mapProvider === "qiMap") {
          tripMapRef.removeLayerSource(activeLayerRef.current);
        } else if (mapProvider === "mapbox") {
          const lineId = `${activeLayerRef.current}_line`;
          if (tripMapRef.getLayer?.(lineId)) tripMapRef.removeLayer(lineId);
          if (tripMapRef.getSource?.(lineId)) tripMapRef.removeSource(lineId);
        }
      } catch (e) {}
      activeLayerRef.current = null;
    }
  }, [selectedRange, deviceId]);

  // Reset map data
  useEffect(() => {
    setmapTrackData([]);
    setIsLoading(true);
    lastProcessedResDataRef.current = null; // Reset processed data tracker
    fittedDatasetKeyRef.current = null;
  }, [refreshMap]);

  // Fetch trip map data using the custom hook
  const {
    data: resData,
    error,
    isLoading: isApiLoading,
  } = useGetVehicleRoutingQuery(
    deviceId && {
      deviceId,
      per_page: totalCount,
      page,
      start_time: moment(selectedRange?.startDate || selectedRange?.start_time).valueOf(),
      end_time: moment(selectedRange?.endDate || selectedRange?.end_time).valueOf(),
      isTime: true,
      plate_number: selectedPlateNumber,
      type: "Vehicles",
      // Trips map keeps ascending route order (only the live summary loads the
      // newest page first - design "Route Loading Strategy").
      sortOrder: "ASC",
    },
    // { refetchOnMountOrArgChange: true },
    { skip: listCount === 0 }
  );

  const showAddress = (device_data) => {
    if (device_data?.gps?.position?.address) {
      return `${device_data?.gps?.position?.address?.city},${device_data?.gps?.position?.address?.region},${device_data?.gps?.position?.address?.country}`;
    } else {
      return "No Recorded Data";
    }
  };

  // Collect and process map data
  useEffect(() => {
    if (!deviceId || !resData) {
      setIsLoading(false);
      return;
    }

    // Guard: don't re-process the same resData reference (prevents page counter
    // from jumping ahead when the effect re-fires due to other dependency changes)
    if (resData === lastProcessedResDataRef.current) {
      return;
    }
    lastProcessedResDataRef.current = resData;

    if (page > Math.round(resData?.total_count / totalCount)) {
      setIsLoading(false);
      return;
    }
    const filteredTrackData = resData?.data
      .filter(
        (device_data) => device_data?.gps && device_data?.gps?.position?.lat && device_data?.gps?.position?.lng && (device_data?.gps?.hdop == null || device_data?.gps?.hdop <= maxHdopThreshold)
      )
      .map((device_data) => ({
        source_id: device_data?.source_id,
        lat: device_data?.gps?.position?.lat,
        lng: device_data?.gps?.position?.lng,
        direction: device_data?.velocity?.direction,
        speed: device_data?.velocity?.speed,
        "010d_vehicle_speed": device_data?.obd?.["010d_vehicle_speed"],
        address: showAddress(device_data),
        timestamp: moment(device_data?.source_time).valueOf(),
        hdop: device_data?.gps?.hdop,
      }));
    if (filteredTrackData && filteredTrackData.length) {
      setmapTrackData((prev) => [...prev, ...filteredTrackData]);
      setPage((c) => c + 1);
    } else if (page === 0) {
      // First page returned no valid GPS points - no data available
      setIsLoading(false);
    } else {
      // Subsequent page returned no valid points, but continue pagination
      setPage((c) => c + 1);
    }
    setIsLoading(false);
  }, [resData, selectedRange, maxHdopThreshold, deviceId, totalCount, showAddress]);

  useEffect(() => {
    if (mapProvider === "qiMap") {
      try {
        if (tripMapRef && validTrackData.length && deviceId) {
          const shouldFit = shouldAutoFitViewport(
            fittedDatasetKeyRef.current,
            viewportDatasetKey
          );
          // Remove the previously drawn layer using the ref (deterministic),
          // not `track_${count - 1}` which can be stale across rapid updates.
          if (activeLayerRef.current) {
            tripMapRef.removeLayerSource(activeLayerRef.current);
          }
          let layerName = `track_${count}`;
          activeLayerRef.current = layerName;

          tripMapRef.addLayer({ name: layerName });
          // Draw the last and first points of the track with specific styles
          tripMapRef.drawTrack({
            layerName: layerName,
            fitWithinView: false,
            trackType: "point",
            data: [{ coordinates: [validTrackData[0]] }],
            style: {
              point: {
                style: (feature) => {
                  return new Style({
                    image: new Icon({
                      crossOrigin: "anonymous",
                      src: destination,
                      imgSize: [44, 44],
                      anchor: [0.5, 0.9],
                      /* rotation: feature.direction, */
                    }),
                  });
                },
              },
            },
          });
          tripMapRef.drawTrack({
            layerName: layerName,
            fitWithinView: false,
            trackType: "point",
            data: [{ coordinates: [validTrackData[validTrackData.length - 1]] }],
            style: {
              point: {
                style: (feature) => {
                  return new Style({
                    image: new Icon({
                      crossOrigin: "anonymous",
                      src: homeLocation,
                      imgSize: [36, 36],
                      anchor: [0.5, 0.9],
                      /* rotation: feature.direction, */
                    }),
                  });
                },
              },
            },
          });
          // Draw the track line connecting all points
          tripMapRef.drawTrack({
            layerName: layerName,
            fitWithinView: false,
            trackType: "line",
            data: [{ coordinates: validTrackData }],
            style: {
              line: {
                style: (feature) => {
                  return new Style({
                    stroke: new Stroke({
                      color: "#0767d0",
                      width: 5,
                    }),
                  });
                },
              },
            },
            tooltip: {
              showOn: "click",
              content: (feature) => {
                let { coordinate } = feature.get("data");
                return `
                <div>Address: ${coordinate?.address}</div>
                <div>Latitude: ${Number(coordinate?.lat).toFixed(5)}</div>
                <div> Longitude: ${Number(coordinate?.lng).toFixed(5)} </div>
                <div>Direction: ${coordinate?.direction}</div>
                <div>Speed: ${Number(coordinate?.speed * 3.6).toFixed(1)} km/h</div>
                `;
              },
            },
          });
          if (shouldFit) {
            tripMapRef.performFit(layerName);
            fittedDatasetKeyRef.current = viewportDatasetKey;
          }
          setCount((c) => c + 1);
        } else {
          if (activeLayerRef.current) {
            tripMapRef?.removeLayerSource(activeLayerRef.current);
            activeLayerRef.current = null;
          }
          tripMapRef?.resetZoomLevel();
        }
      } catch (e) {}
    }
    if (mapProvider === "mapbox") {
      try {
        // Helper function to remove a layer and its source
        const removeLayerAndSource = (id) => {
          if (tripMapRef.getLayer(id)) {
            tripMapRef.removeLayer(id);
          }
          if (tripMapRef.getSource(id)) {
            tripMapRef.removeSource(id);
          }
        };

        // Function to clear all previous layers and sources. Uses the ref so it
        // removes the layer drawn by the PREVIOUS render (a local array would
        // always start empty and never clean up the accumulated track).
        const clearPreviousLayers = () => {
          if (activeLayerRef.current) {
            removeLayerAndSource(`${activeLayerRef.current}_line`);
            activeLayerRef.current = null;
          }
        };

        // Function to clear previous markers
        const clearPreviousMarkers = () => {
          if (window.startMarker) {
            window.startMarker.remove();
            window.startMarker = null;
          }
          if (window.endMarker) {
            window.endMarker.remove();
            window.endMarker = null;
          }
        };

        if (tripMapRef && validTrackData.length && deviceId) {
          const shouldFit = shouldAutoFitViewport(
            fittedDatasetKeyRef.current,
            viewportDatasetKey
          );
          const layerName = `track_${count}`;

          // Clear previous layers and markers
          clearPreviousLayers();
          clearPreviousMarkers();

          // Remember this layer so the next render can clean it up.
          activeLayerRef.current = layerName;

          // Add track line as a source
          tripMapRef.addSource(`${layerName}_line`, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: validTrackData.map((point) => [point.lng, point.lat]),
              },
            },
          });

          tripMapRef.addLayer({
            id: `${layerName}_line`,
            type: "line",
            source: `${layerName}_line`,
            paint: {
              "line-color": "#0767d0", // Match qiMap blue color
              "line-width": 5,
            },
          });

          // Fit the map to the track bounds
          const bounds = validTrackData.reduce(
            (bounds, coord) => bounds.extend([coord.lng, coord.lat]),
            new mapboxgl.LngLatBounds(
              [validTrackData[0].lng, validTrackData[0].lat],
              [validTrackData[0].lng, validTrackData[0].lat]
            )
          );

          if (shouldFit) {
            tripMapRef.fitBounds(bounds, { padding: 20 });
            fittedDatasetKeyRef.current = viewportDatasetKey;
          }

          // Helper function to create custom marker elements
          const createCustomMarkerElement = (imageSrc, size) => {
            const element = document.createElement("div");
            element.className = "custom-marker";
            element.style.backgroundImage = `url(${imageSrc})`;
            element.style.width = `${size}px`;
            element.style.height = `${size}px`;
            element.style.backgroundSize = "contain";
            element.style.backgroundRepeat = "no-repeat";
            element.style.backgroundPosition = "center";
            return element;
          };

          // Add start and end markers with custom icons to match qiMap
          window.startMarker = new mapboxgl.Marker({
            element: createCustomMarkerElement(destination, 44),
          })
            .setLngLat([validTrackData[0].lng, validTrackData[0].lat])
            .addTo(tripMapRef);

          window.endMarker = new mapboxgl.Marker({
            element: createCustomMarkerElement(homeLocation, 36),
          })
            .setLngLat([
              validTrackData[validTrackData.length - 1].lng,
              validTrackData[validTrackData.length - 1].lat,
            ])
            .addTo(tripMapRef);

          // Increment the count for the next layer
          setCount((c) => c + 1);
        } else {
          // Clear all layers and markers if no valid data
          clearPreviousLayers();
          clearPreviousMarkers();
        }
      } catch (e) {
        console.error("Error:", e);
      }
    }
    if (mapProvider === "google") {
      try {
        if (tripMapRef && validTrackData.length && deviceId) {
          const shouldFit = shouldAutoFitViewport(
            fittedDatasetKeyRef.current,
            viewportDatasetKey
          );
          let layerName = `track_${count}`;

          // Remove previous tracks (if they exist)
          for (let i = 0; i < count; i++) {
            const previousTrack = window[`track_${i}_google`];
            if (previousTrack) {
              previousTrack.forEach((overlay) => overlay.setMap(null));
            }
          }

          // Create start and end markers with custom icons to match qiMap
          const startMarker = new google.maps.Marker({
            position: { lat: validTrackData[0].lat, lng: validTrackData[0].lng },
            map: tripMapRef,
            icon: {
              url: destination,
              scaledSize: new google.maps.Size(44, 44),
            },
          });

          const endMarker = new google.maps.Marker({
            position: {
              lat: validTrackData[validTrackData.length - 1].lat,
              lng: validTrackData[validTrackData.length - 1].lng,
            },
            map: tripMapRef,
            icon: {
              url: homeLocation,
              scaledSize: new google.maps.Size(36, 36),
            },
          });

          // Create a polyline with custom styling to match qiMap
          const trackLine = new google.maps.Polyline({
            path: validTrackData.map((point) => ({
              lat: point.lat,
              lng: point.lng,
            })),
            geodesic: true,
            strokeColor: "#0767d0",
            strokeOpacity: 1.0,
            strokeWeight: 5,
          });

          trackLine.setMap(tripMapRef);

          // Store the current track
          window[`track_${count}_google`] = [startMarker, endMarker, trackLine];

          // Adjust the map bounds to fit the track
          const bounds = new google.maps.LatLngBounds();
          validTrackData.forEach((point) =>
            bounds.extend(new google.maps.LatLng(point.lat, point.lng))
          );
          if (shouldFit) {
            tripMapRef.fitBounds(bounds);
            fittedDatasetKeyRef.current = viewportDatasetKey;
          }

          // Increment the count for the next layer
          setCount((c) => c + 1);
        } else {
          // Clear all previous Google Maps markers and lines
          for (let i = 0; i < count; i++) {
            const previousTrack = window[`track_${i}_google`];
            if (previousTrack) {
              previousTrack.forEach((overlay) => overlay.setMap(null));
            }
          }
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [tripMapRef, validTrackData, deviceId, dockAlign, mapProvider]);

  return (
    <div className={`map-container`}>
      <QIMapLoader
        mapExpand={mapExpand}
        dockAlign={dockAlign}
        setMapRef={setTripMapRef}
        containerId="device-map-container"
        maximizeMinimize={maximizeMinimize}
        mapProvider={mapProvider}
        mapboxAccessToken={mapboxAccessToken}
        googleMapsApiKey={googleMapsApiKey}
      />
      {(isLoading || isApiLoading || mapSelectionLoading) && (deviceId || selectedPlateNumber) && (
        <div className="map-loader-overlay">
          <div className="map-loader-content">
            <QISpinner size="60px" />
            <p>{mapSelectionLoading ? "Loading Selected Trip..." : "Loading Map..."}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripsMap;
